import React, { useEffect, useState } from 'react';
import { Card } from 'Components/Card';
import { FormProvider, useForm } from 'react-hook-form';
import { hbtResolver } from '@hobt/schema-validator';
import { applicableRatesSchema } from '@hobt/claim-domain';
import FormNumber from 'Components/Inputs/FormNumber';
import styles from './styles.module.scss';
import { Button } from 'Components/Common/Button';
import { CardBody } from 'Components/Card/CardBody';
import { CardFooter } from 'Components/Card/CardFooter';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { updateApplicableRates } from '../ProvincialApplicablesRates.Service';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { ProvincialApplicableRatesFormProps } from '../types';
import FormDatepicker from 'Components/Inputs/FormDatepicker';
import { ToastNotification } from 'Feature/CommonComponents/ContentComponents';
import { ApplicationStates } from 'Components/Enums/ApplicationStatus';
import { isUserInRoles } from 'Components/Common/UserHelpers/CheckUserRole';
import { ModuleMapping, UserRole } from '@hobt/constants';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { HbtSitecoreContextType } from 'Foundation/HydrateSitecoreContext';
import { useFeature } from 'flagged';
import { FeatureFlags } from 'Feature/Enums/FeatureFlag.enum';
import i18n from 'i18next';

const ProvincialApplicableRatesData = {
  codeEffectiveDate: '',
  provinceID: '',
  codeLastEffectiveDate: '',
  goodsAndServiceTaxRealEstateRatePercent: '',
  harmonizedSalesTaxRealEstateRatePercent: '',
  holdingDayCount: '',
  legalActionTimeOfDayCount: '',
  maximumAppraisalEditAmount: '',
  maximumEditAssignmentAmount: '',
  maximumEditDeficitAmount: '',
  maximumEditTitleAmount: '',
  maximumUtilityEditAmount: '',
  propertyManagementFeeAmount: '',
  provincialSalesTaxRealEstateRatePercent: '',
  realEstateCommissionRate: '',
  taxRealEstateRate: '',
  recordCreatedTimestamp: '',
  recordUpdatedTimestamp: '',
  recordUpdatedUserID: '',
  futureEffectiveDate: ''
};

const ProvincialApplicableRatesForm = ({ fields, rates }: ProvincialApplicableRatesFormProps) => {
  const authenticationContext = useAuthenticationContext();
  const [toastState, setToastState] = useState<ApplicationStates>(ApplicationStates.DEFAULT);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string>('');
  const [tomorrow, setTomorrow] = useState<Date>(new Date());
  const [futureDate, setFutureDate] = useState<Date>(new Date());
  const sitecoreContextFactory = useSitecoreContext();
  const sitecoreContext = sitecoreContextFactory?.sitecoreContext as HbtSitecoreContextType;
  const siteTypeIsInternal = useFeature(FeatureFlags.INTERNAL);
  const isBusinessUser =
    isUserInRoles(
      ModuleMapping.claim,
      [UserRole.BusinessUser],
      sitecoreContext?.user?.moduleRoleMapping
    ) && siteTypeIsInternal === true;
  const methods = useForm({
    resolver: hbtResolver(applicableRatesSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
    defaultValues: { ...ProvincialApplicableRatesData }
  } as Record<string, any>);

  const onToastCloseHandler = () => {
    setShowToast(false);
    methods.reset(rates);
  };
  useEffect(() => {
    methods.reset(rates);
  }, [rates]);

  useEffect(() => {
    const today = new Date();
    tomorrow.setDate(today.getDate() + 1);
    futureDate.setDate(tomorrow.getDate() + 90);
  }, []);

  const onSubmit = async (data: any, e: any) => {
    e.preventDefault();
    try {
      const responce = await updateApplicableRates(authenticationContext, data);
      if (responce?.status == 200) {
        setShowToast(true);
        setToastState(ApplicationStates.SUCCESS);
        setToastMsg(i18n.t('Globals-Toast-Success-Item-Saved'));
      }
    } catch (e) {
      setShowToast(true);
      setToastState(ApplicationStates.ERROR);
      setToastMsg(i18n.t('DefaultSubmission-OtherErrors'));
    }
  };

  return (
    <>
      <ToastNotification
        type={toastState}
        isActive={showToast}
        title={
          toastState === ApplicationStates.ERROR
            ? i18n.t('DefaultSubmission-ErrorToastTitle')
            : i18n.t('DefaultActions-SystemSuccessToast-Action-Title')
        }
        content={toastMsg}
        onCloseCallback={() => onToastCloseHandler()}
      />
      <Card>
        <FormProvider {...methods}>
          <fieldset disabled={isBusinessUser}>
            <form
              id="applicableRatesForm"
              className="form applicable-rates-form"
              onSubmit={methods.handleSubmit(onSubmit)}
              noValidate
            >
              <CardBody>
                <div className="row card__body-row">
                  <div className="form__element form__element--2-column col-6">
                    <FormDatepicker
                      className={styles.halfLeft}
                      label={fields.effectiveDate}
                      name="futureEffectiveDate"
                      isReadOnly={false}
                      startYear={tomorrow.toDateString()}
                      endYear={futureDate.toDateString()}
                    />
                  </div>
                  <div className="form__element form__element--2-column col-6">
                    <FormDatepicker
                      className={styles.halfLeft}
                      label={fields.codeEffectiveDate}
                      name="codeEffectiveDate"
                      isReadOnly={true}
                      startYear="1960-01-02"
                    />
                  </div>
                </div>
                <br></br>
                <hr></hr>
                <h2 className="card-title">
                  <Text field={fields.realestateAndCommisionTitle} />
                </h2>
                <hr />
                <div className="row card__body-row">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`taxRealEstateRate`}
                      label={fields.realestateRate}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`goodsAndServiceTaxRealEstateRatePercent`}
                      label={fields.gstTaxOnRealestate}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`provincialSalesTaxRealEstateRatePercent`}
                      label={fields.pstTaxOnRealestate}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`harmonizedSalesTaxRealEstateRatePercent`}
                      label={fields.hstTaxonRealestate}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`realEstateCommissionRatePercent`}
                      label={fields.rEstateCommRatePaid}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <hr />
                <h2 className="card-title">
                  <Text field={fields.propertyManagementTitle} />
                </h2>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`propertyManagementFeeAmount`}
                      label={fields.propMgtFlatFee}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <br></br>
                <h2 className="card-title">
                  <Text field={fields.legalTitle} />
                </h2>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`legalActionTimeOfDayCount`}
                      label={fields.legalActionTiming}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <br></br>
                <h2 className="card-title">
                  <Text field={fields.taxesTitle} />
                </h2>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`holdingDayCount`}
                      label={fields.holdingDays}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <hr />
                <h2 className="card-title">
                  <Text field={fields.legalFeesAndDisbursement} />
                </h2>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`maximumEditTitleAmount`}
                      label={fields.maxEditForTitle}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`maximumEditAssignmentAmount`}
                      label={fields.maxEditForAssign}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`maximumEditDeficitAmount`}
                      label={fields.maxEditForDefSale}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <hr />
                <h2 className="card-title">
                  <Text field={fields.otherTitle} />
                </h2>
                <div className="row card__body-row mt-3">
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`maximumAppraisalEditAmount`}
                      label={fields.maxAppraislEdit}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                  <div className="form__element form__element--2-column col-6">
                    <FormNumber
                      className={''}
                      name={`maximumUtilityEditAmount`}
                      label={fields.maxUtilityMoEdit}
                      isReadOnly={false}
                      onBlurValidationRequire={true}
                    />
                  </div>
                </div>
                <CardFooter className={`${styles.buttonsLayout} mt-3`} separator>
                  <div>
                    <Button
                      responsive
                      name="updateProvienceRates"
                      text={fields.Update}
                      ariaText={{ value: 'Update' }}
                      type="submit"
                      disabled={false}
                    />
                    <Button
                      secondaryButton
                      responsive
                      name="updateProvienceRatesCancelBtn"
                      text={fields.Cancel}
                      ariaText={{ value: 'Cancel' }}
                      onClick={() => {
                        methods.reset(rates);
                      }}
                      type="button"
                    />
                  </div>
                </CardFooter>
              </CardBody>
            </form>
          </fieldset>
        </FormProvider>
      </Card>
    </>
  );
};

export default ProvincialApplicableRatesForm;
